'use client';

import type { ReactNode } from 'react';
import { cn, interactiveHoverClassName } from '~/lib/utils';

/** Shared Operate chrome for Help / Inbox / User nav popovers. */
export function NavPopoverShell({
  children,
  className,
  width,
}: {
  children: ReactNode;
  className?: string;
  width?: string | number;
}) {
  return (
    <div
      className={cn(
        'bg-popover text-popover-foreground flex max-h-[min(36rem,80vh)] flex-col overflow-hidden rounded-xl border border-border',
        'shadow-[0_4px_14px_rgba(0,0,0,0.07)]',
        className,
      )}
      style={width != null ? { width } : undefined}
    >
      {children}
    </div>
  );
}

export function NavPopoverHeader({
  title,
  meta,
  actions,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="border-border flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="text-foreground truncate text-base font-semibold leading-none tracking-tight">
          {title}
        </h2>
        {meta ? <div className="flex shrink-0 items-center">{meta}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-0.5">{actions}</div> : null}
    </div>
  );
}

export function NavPopoverBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>{children}</div>;
}

export function NavPopoverFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-border shrink-0 border-t bg-popover px-3 py-2', className)}>
      {children}
    </div>
  );
}

export const navPopoverIconButtonClass =
  'text-muted-foreground hover:bg-background hover:text-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** Inbox / menu list rows — light foreground tint (readable on white + muted canvases). */
export const navPopoverListItemClass = cn(
  'group relative flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left text-sm no-underline transition-colors outline-none focus-visible:bg-muted',
  interactiveHoverClassName,
);

export const navPopoverFooterLinkClass = cn(
  'text-foreground flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium no-underline transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
  interactiveHoverClassName,
);
